import { create } from 'zustand';
import { istKonfiguriert } from '@/lib/amplify';
import { fehlerText, istNetzfehler } from './api';
import { METAS, metaSchreiben } from './db';
import { einheitenHeilen } from './einheiten';
import {
  laufendeZuruecksetzen,
  outboxZaehler,
  wartendeProtokolle,
  type OutboxZaehler,
} from './outbox';
import { pullAusfuehren, type PullErgebnis } from './pull';
import { pushAusfuehren } from './push';
import { pruefeSpeicher, type SpeicherStand } from './speicher';

/**
 * Sync-Worker (PHASENPLAN 1.3).
 *
 * Reagiert auf `online`/`offline`, zieht beim Login und bei jedem Reconnect
 * und arbeitet dazwischen die Outbox ab. Die UI blockiert nie auf das Netz —
 * sie liest ausschließlich aus IndexedDB (CLAUDE.md §3.4).
 */

export type SyncZustandsArt =
  'unbekannt' | 'offline' | 'synchronisiert' | 'laeuft' | 'wartet' | 'fehler';

interface SyncStore {
  online: boolean;
  art: SyncZustandsArt;
  zaehler: OutboxZaehler;
  wartendeProtokolle: number;
  letzterSync: string | null;
  letzterFehler: string | null;
  speicher: SpeicherStand | null;
  fortschritt: { erledigt: number; gesamt: number } | null;
  setze: (teil: Partial<SyncStore>) => void;
}

const LEERER_ZAEHLER: OutboxZaehler = {
  offen: 0,
  laeuft: 0,
  fehler: 0,
  aufgegeben: 0,
  gesamt: 0,
};

export const useSync = create<SyncStore>((set) => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  art: 'unbekannt',
  zaehler: LEERER_ZAEHLER,
  wartendeProtokolle: 0,
  letzterSync: null,
  letzterFehler: null,
  speicher: null,
  fortschritt: null,
  setze: (teil) => set(teil),
}));

let laeuft = false;
let geplant: ReturnType<typeof setTimeout> | null = null;
let intervall: ReturnType<typeof setInterval> | null = null;

/**
 * Abstand zwischen zwei Pulls im laufenden Betrieb.
 *
 * Der 30-Sekunden-Takt sendet nur. Ohne diesen Abstand wurde erst beim nächsten
 * Reconnect wieder gezogen — Änderungen von anderen Geräten (oder vom Server,
 * etwa ein neuer PDF-Schlüssel) tauchten dann stundenlang nicht auf.
 */
const PULL_ABSTAND_MS = 5 * 60_000;
let letzterPullMs = 0;

export const zustandAktualisieren = async (): Promise<void> => {
  const zaehler = await outboxZaehler();
  const wartend = await wartendeProtokolle();
  const speicher = await pruefeSpeicher();
  const store = useSync.getState();

  /*
   * Ein Eintrag im Backoff („fehler“) zählt hier bewusst als Fehlerzustand.
   * Vorher sah ein Eintrag, der bei jedem Versuch abgewiesen wird, genauso aus
   * wie einer, der gerade erst geschrieben wurde — grau und wartend. Erst nach
   * zwölf Versuchen wurde er rot, das dauert eine halbe Stunde.
   */
  const art: SyncZustandsArt = !store.online
    ? 'offline'
    : laeuft
      ? 'laeuft'
      : zaehler.aufgegeben > 0 || zaehler.fehler > 0
        ? 'fehler'
        : zaehler.gesamt > 0
          ? 'wartet'
          : 'synchronisiert';

  store.setze({ zaehler, wartendeProtokolle: wartend, speicher, art });
};

export interface SyncErgebnis {
  gesendet: number;
  fehlgeschlagen: number;
  aufgegeben: number;
  pull: PullErgebnis | null;
  fehler: string | null;
}

/**
 * Ein vollständiger Durchlauf: erst senden, dann ziehen.
 *
 * Die Reihenfolge ist wichtig — würde zuerst gezogen, könnte der Pull einen
 * lokalen Stand überschreiben, der noch gar nicht beim Server war.
 */
export const synchronisieren = async (mitPull = true): Promise<SyncErgebnis> => {
  const store = useSync.getState();

  if (!istKonfiguriert()) {
    return { gesendet: 0, fehlgeschlagen: 0, aufgegeben: 0, pull: null, fehler: null };
  }
  if (laeuft) {
    return { gesendet: 0, fehlgeschlagen: 0, aufgegeben: 0, pull: null, fehler: null };
  }
  if (!store.online) {
    store.setze({ art: 'offline' });
    return { gesendet: 0, fehlgeschlagen: 0, aufgegeben: 0, pull: null, fehler: null };
  }

  laeuft = true;
  store.setze({ art: 'laeuft', letzterFehler: null });

  try {
    const push = await pushAusfuehren();
    let pull: PullErgebnis | null = null;
    if (mitPull) {
      pull = await pullAusfuehren();
      letzterPullMs = Date.now();
    }

    const jetzt = new Date().toISOString();
    await metaSchreiben(METAS.letzterSync, jetzt);
    store.setze({ letzterSync: jetzt, fortschritt: null });

    return { ...push, pull, fehler: null };
  } catch (fehler) {
    const text = fehlerText(fehler);
    if (istNetzfehler(fehler)) {
      useSync.getState().setze({ online: false, art: 'offline' });
    } else {
      useSync.getState().setze({ letzterFehler: text, art: 'fehler' });
    }
    return { gesendet: 0, fehlgeschlagen: 0, aufgegeben: 0, pull: null, fehler: text };
  } finally {
    laeuft = false;
    await zustandAktualisieren();
  }
};

/** Stößt einen Durchlauf an, ohne mehrere gleichzeitig zu starten. */
export const syncAnstossen = (verzoegerungMs = 800): void => {
  if (geplant) clearTimeout(geplant);
  geplant = setTimeout(() => {
    geplant = null;
    void synchronisieren(false);
  }, verzoegerungMs);
};

let abmelden: (() => void) | null = null;

/** Startet den Worker. Rückgabe beendet ihn wieder. */
export const syncStarten = (): (() => void) => {
  if (abmelden) return abmelden;

  const store = useSync.getState();

  const beiOnline = (): void => {
    store.setze({ online: true });
    // Reconnect: senden und anschließend ziehen (SPEC Kap. 4).
    void synchronisieren(true);
  };

  const beiOffline = (): void => {
    store.setze({ online: false, art: 'offline' });
  };

  const beiSichtbar = (): void => {
    if (document.visibilityState === 'visible' && navigator.onLine) syncAnstossen(400);
  };

  window.addEventListener('online', beiOnline);
  window.addEventListener('offline', beiOffline);
  document.addEventListener('visibilitychange', beiSichtbar);

  // Regelmäßiger Takt, damit Backoff-Einträge auch ohne Ereignis drankommen.
  // Alle fünf Minuten wird dabei zusätzlich gezogen.
  intervall = setInterval(() => {
    if (!navigator.onLine) return;
    void synchronisieren(Date.now() - letzterPullMs > PULL_ABSTAND_MS);
  }, 30_000);

  // Auch offline: Standardeinheiten anlegen und verwaiste Räume einhängen,
  // damit die Einheitenebene ohne Netz vollständig ist.
  void laufendeZuruecksetzen()
    .then(() => einheitenHeilen())
    .then(() => zustandAktualisieren());

  abmelden = () => {
    window.removeEventListener('online', beiOnline);
    window.removeEventListener('offline', beiOffline);
    document.removeEventListener('visibilitychange', beiSichtbar);
    if (intervall) clearInterval(intervall);
    if (geplant) clearTimeout(geplant);
    intervall = null;
    geplant = null;
    abmelden = null;
  };

  return abmelden;
};

/**
 * Warnt vor dem Schließen, solange ungesendete Änderungen offen sind
 * (SPEC Kap. 4). Rückgabe entfernt den Wächter wieder.
 */
export const schliessWaechterStarten = (): (() => void) => {
  const beiVerlassen = (ereignis: BeforeUnloadEvent): void => {
    const { zaehler, online } = useSync.getState();
    if (zaehler.gesamt > 0 && !online) {
      ereignis.preventDefault();
    }
  };
  window.addEventListener('beforeunload', beiVerlassen);
  return () => window.removeEventListener('beforeunload', beiVerlassen);
};

/** Anzeigetext für die Kopfzeile (SPEC Kap. 4). */
export const syncText = (zustand: {
  art: SyncZustandsArt;
  zaehler: OutboxZaehler;
  wartendeProtokolle: number;
  fortschritt: { erledigt: number; gesamt: number } | null;
}): string => {
  switch (zustand.art) {
    case 'offline':
      return zustand.wartendeProtokolle > 0
        ? `Offline — ${zustand.wartendeProtokolle} ${zustand.wartendeProtokolle === 1 ? 'Protokoll wartet' : 'Protokolle warten'}`
        : 'Offline';
    case 'laeuft':
      return zustand.fortschritt
        ? `Synchronisiere ${zustand.fortschritt.erledigt}/${zustand.fortschritt.gesamt}`
        : 'Synchronisiere …';
    case 'wartet':
      return `${zustand.zaehler.gesamt} ${zustand.zaehler.gesamt === 1 ? 'Änderung wartet' : 'Änderungen warten'}`;
    case 'fehler': {
      if (zustand.zaehler.aufgegeben > 0) {
        return `${zustand.zaehler.aufgegeben} ${zustand.zaehler.aufgegeben === 1 ? 'Eintrag konnte' : 'Einträge konnten'} nicht gesendet werden`;
      }
      // Noch nicht aufgegeben: Der nächste Versuch läuft, der Grund steht im Dialog.
      return `${zustand.zaehler.fehler} ${zustand.zaehler.fehler === 1 ? 'Sendeversuch' : 'Sendeversuche'} fehlgeschlagen — Details antippen`;
    }
    case 'synchronisiert':
      return 'Aktuell';
    default:
      return 'Status unbekannt';
  }
};
